import {InfoCircleOutlined} from '@ant-design/icons';
import {Table} from 'antd';
import {CardProps} from 'antd/es/card';
import {ColumnProps} from 'antd/lib/table';
import {Document, useDocumentsQuery, useOnUpdateDocumentsSubscription} from 'api/generated';
import {Hint, Stack} from 'components';
import {DocumentStatusTag} from 'components/DocumentStatusTag';
import {StatusTag} from 'components/StatusTag';
import {TableCard} from 'components/TableCard';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {colors} from 'lib/colors';
import {getContentTypeIcon} from 'lib/contentTypeIcons';
import {getDocumentName} from 'lib/documentUtils';
import moment from 'moment';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import {DocumentActions} from 'routes/Account/Documents/DocumentActions';
import {UploadDocument} from './UploadDocument';

export type Props = {
  accountId: string;
  cardProps: CardProps;
};

export const Documents: React.FC<Props> = ({accountId, cardProps}) => {
  const {isAdmin} = useAccessLevel();
  const [size, setSize] = useState(10);
  const [from, setFrom] = useState(0);
  const history = useHistory();
  const {
    data: documentsData,
    loading: loadingDocuments,
    refetch
  } = useDocumentsQuery({
    variables: {accountId}
  });

  const documents = documentsData?.documents.items || [];
  const total = documents.length || 0;

  useOnUpdateDocumentsSubscription({
    variables: {accountId},
    onSubscriptionData: () => {
      return refetch();
    }
  });

  const columns: Array<ColumnProps<Document>> = [
    {
      title: 'Name',
      dataIndex: 'type',
      className: isAdmin ? 'clickable' : undefined,
      onCell: isAdmin
        ? (document) => ({
            onClick: (e) => {
              e.preventDefault();
              const url = `/accounts/${accountId}/documents/${document.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, document.id)?.focus();
              } else {
                history.push(url);
              }
            }
          })
        : undefined,
      render: (_, doc) => (
        <Stack gutter={5}>
          {getContentTypeIcon(doc.contentType)}
          <span>
            <Hint
              message={doc.note}
              icon={<InfoCircleOutlined style={{color: colors.secondary}} />}>
              {getDocumentName(doc)}
            </Hint>
          </span>
        </Stack>
      )
    },
    {
      dataIndex: 'internal',
      className: 'minimized-cell',
      render: (value) => (value ? <StatusTag>internal</StatusTag> : undefined)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render: (value, document) => <DocumentStatusTag document={document} />,
      sorter: (a, b) => {
        if (!a.status || !b.status) return 0;
        return a.status.localeCompare(b.status);
      }
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      defaultSortOrder: 'descend',
      render: (date: string) => moment.utc(date).format('ll'),
      sorter: (a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return moment.utc(a.createdAt).diff(moment.utc(b.createdAt));
      }
    }
  ];

  if (isAdmin) {
    columns.push({
      title: '',
      key: 'actions',
      align: 'right',
      className: 'minimized-cell',
      render: (_, document) => <DocumentActions document={document} key={document.status} />
    });
  }

  return (
    <TableCard title="Documents" {...cardProps} extra={<UploadDocument accountId={accountId} />}>
      <Table
        dataSource={documents}
        columns={columns as any}
        loading={loadingDocuments}
        rowKey="id"
        pagination={{
          total,
          pageSize: size,
          hideOnSinglePage: true,
          current: from / size + 1,
          onShowSizeChange: (current, pageSize) => {
            setSize(pageSize);
            setFrom((current - 1) * pageSize);
          },
          onChange: (page, size = 0) => setFrom((page - 1) * size)
        }}
      />
    </TableCard>
  );
};
